import { useScrollReveal } from '@/hooks/useScrollReveal';

const services = [
  {
    icon: 'ri-drop-line',
    title: 'Cleaning & Sorting',
    description: 'Produce and seafood are washed, de-veined, de-shelled, and sorted by size and quality grade. Foreign material detection ensures only premium product enters the chain.',
    accent: 'bg-secondary-100 text-secondary-600',
    border: 'border-secondary-200',
    accentBar: 'bg-secondary-400',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Grading',
    description: 'Every batch is graded to USDA-equivalent standards — A-Grade, B-Grade, and Commercial — so buyers always know exactly what they are purchasing.',
    accent: 'bg-primary-100 text-primary-600',
    border: 'border-primary-200',
    accentBar: 'bg-primary-400',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Weighing & Portioning',
    description: 'Precision weighing down to the gram, with portion-controlled cuts for retail packs and bulk-format weighing for wholesale trade — no short-weight, no disputes.',
    accent: 'bg-accent-100 text-accent-700',
    border: 'border-accent-200',
    accentBar: 'bg-accent-400',
  },
  {
    icon: 'ri-secure-payment-line',
    title: 'Vacuum Packaging',
    description: 'Multi-layer vacuum-sealed packaging extends shelf life, prevents freezer burn, and locks in freshness. Every pack is labeled with batch codes, dates, and traceability QR.',
    accent: 'bg-secondary-100 text-secondary-700',
    border: 'border-secondary-200',
    accentBar: 'bg-secondary-400',
  },
  {
    icon: 'ri-barcode-line',
    title: 'SKU Standardisation',
    description: 'Every product is cut, packed, and catalogued to a standard SKU — same spec in Kochi as in Delhi. Buyers order by SKU code, not description, eliminating quality disputes.',
    accent: 'bg-primary-100 text-primary-600',
    border: 'border-primary-200',
    accentBar: 'bg-primary-400',
  },
];

export default function ProcessingServices() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="services" className="py-20 md:py-28 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Core Services
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Five Steps to a Trade-Ready SKU
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto">
            From raw harvest to a standardised, packaged product — every step is measured, documented, and verified.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className={`group relative bg-card-50 rounded-xl p-6 md:p-8 border ${service.border} shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate`}
            >
              <div className={`absolute top-0 left-5 right-5 h-1 rounded-full ${service.accentBar} opacity-40 group-hover:opacity-100 transition-opacity`}></div>

              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${service.accent} mb-5 animate-icon-float icon-hover-pop`}>
                <i className={`${service.icon} text-xl`}></i>
              </div>

              <h3 className="font-heading font-bold text-primary-700 text-lg mb-3">
                {service.title}
              </h3>

              <p className="text-sm text-foreground-500 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}